import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AuthImage } from '@/components/auth-image';
import { ApiError } from '@/lib/api';
import { use_pet_photo_mutations, use_pet_photos } from '@/features/pet/hooks';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

export function PetPhotosSection({ pet_id }: { pet_id: string }) {
  const photos_q = use_pet_photos(pet_id);
  const { upload, set_primary, remove } = use_pet_photo_mutations(pet_id);
  const file_input = useRef<HTMLInputElement>(null);
  const [error, set_error] = useState<string | null>(null);

  async function handle_select(e: React.ChangeEvent<HTMLInputElement>) {
    set_error(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      await upload.mutateAsync(file);
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Upload failed');
    }
  }

  const photos = photos_q.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Label>Photos</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => file_input.current?.click()}
          disabled={upload.isPending}
          data-testid="pet-photo-upload"
        >
          {upload.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImagePlus className="h-4 w-4" />
          )}
          Add photo
        </Button>
        <input
          ref={file_input}
          type="file"
          accept={PHOTO_ACCEPT}
          className="hidden"
          data-testid="pet-photo-input"
          onChange={handle_select}
        />
      </div>

      {error && (
        <p className="text-xs text-destructive" data-testid="pet-photo-error">
          {error}
        </p>
      )}

      {photos.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="pet-photos-empty">
          No photos yet. Add one so providers recognise your pet.
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {photos.map((p) => (
            <li
              key={p.id}
              data-testid={`pet-photo-${p.id}`}
              className="group relative overflow-hidden rounded-input border-2 border-border/60 bg-card shadow-clay-sm"
            >
              <AuthImage
                path={`${p.file_path}?variant=thumb`}
                alt="Pet photo"
                className="aspect-square w-full object-cover"
              />
              {p.is_primary && (
                <span
                  className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground"
                  data-testid={`pet-photo-primary-${p.id}`}
                >
                  <Star className="h-3 w-3 fill-current" /> Primary
                </span>
              )}
              <div className="flex items-center justify-between gap-1 p-1.5">
                {!p.is_primary ? (
                  <button
                    type="button"
                    onClick={() => set_primary.mutate(p.id)}
                    disabled={set_primary.isPending}
                    data-testid={`pet-photo-set-primary-${p.id}`}
                    className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    Set primary
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={() => remove.mutate(p.id)}
                  disabled={remove.isPending}
                  aria-label="Delete photo"
                  data-testid={`pet-photo-delete-${p.id}`}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
